/**
 * Capitalizes the first letter of a string.
 * Prefer Tailwind's `first-letter:uppercase` when capitalization is presentational only.
 *
 * @param str - String to capitalize
 * @returns String with first letter capitalized
 */
export const capitalize = (str: string = ''): string => {
    return str.charAt(0).toUpperCase() + str.slice(1)
}

export const getStringParam = (value: string | string[] | undefined): string => {
    return typeof value === 'string' ? value : ''
}

/**
 * Replaces `{{placeholder}}` tokens in `template` with values from `variables`.
 * Unknown tokens are left unchanged.
 *
 * @example interpolateDoubleBraceTemplate('Beste {{nameProviderCapitalized}} {{inCity}}', { nameProviderCapitalized: 'Loodgieter', inCity: 'in Amsterdam' })
 */
export const interpolateDoubleBraceTemplate = <T extends object>(
    template: string,
    variables: T,
): string => {
    return template.replace(/\{\{(\w+)\}\}/g, (match, key: string) => {
        const value = (variables as Record<string, unknown>)[key]
        return typeof value === 'string' ? value : match
    })
}

/**
 * Removes the `*` emphasis markers consumed by the `Highlighter` component.
 *
 * @example removeHighlighter('Mijn *aanvragen*') // 'Mijn aanvragen'
 */
export const removeHighlighter = (value: string): string => {
    return value.replace(/\*/g, '')
}

/** Surrounds a string with ASCII double quotation marks. */
export const wrapInQuotes = (value: string): string => {
    return `"${value}"`
}

/**
 * Strips a trailing ellipsis, and any trailing whitespace, from a label. Matches both spellings the
 * locale files use: three periods and the single `…` character. Use it where the ellipsis is
 * rendered separately from the label, so the two do not double up.
 *
 * A single trailing period is sentence punctuation, not an ellipsis, so it survives.
 *
 * @example withoutTrailingEllipsis('Opslaan...') // 'Opslaan'
 */
export const withoutTrailingEllipsis = (label: string): string => {
    return label.replace(/(\.{3}|…)?\s*$/, '')
}

/**
 * Returns the uppercase initials from the first and last whitespace-separated parts of a name.
 */
export const getNameInitials = (name: string = ''): string => {
    const nameParts = name.trim().split(/\s+/)

    if (!nameParts[0]) {
        return ''
    }

    const firstInitial = nameParts[0].charAt(0)
    const lastInitial = nameParts.length > 1 ? nameParts[nameParts.length - 1].charAt(0) : ''

    return `${firstInitial}${lastInitial}`.toUpperCase()
}

/** Encodes JSON for endpoints that decode base64 before URI-decoding the payload. */
export const encodeUriComponentJson = (value: unknown): string => {
    const json = JSON.stringify(value)

    if (json === undefined) {
        throw new TypeError('The value cannot be represented as JSON')
    }

    return btoa(encodeURIComponent(json))
}
